/**
 * §9 Layer 2 in the browser — the rule that fires on a transition tap.
 *
 * WHY THIS IS NOT ON THE SERVER, where the rest of the safety decisions live.
 * The transition screen's rating tap is also the tap that starts the exercise,
 * and starting an exercise means entering full screen, which browsers only
 * permit inside a user gesture. Anything awaited between the tap and the
 * request spends the gesture, and the exercise then opens in a window instead
 * of full screen — on a patient's laptop, with the stop button somewhere in the
 * page rather than on the screen they are looking at. So the decision has to be
 * synchronous, which means it has to be here.
 *
 * The server is told what happened and owns every consequence that outlasts
 * today (§9 Layer 5). It does not re-derive this rule: the browser is already
 * trusted for `stopReason` and for the stop button itself, and a browser that
 * wanted to suppress a stop could simply not send anything at all. Nothing here
 * decides anything the patient could not decide by closing the laptop.
 *
 * The thresholds are the doctor's, sent with the session. Nothing on any screen
 * displays them.
 */

import type { RunnerSafety, SymptomRating } from '../../api.js';

export type { RunnerSafety };

/**
 * Why a session ended. Stored on the run and on the safety event, so these
 * strings are permanent — do not rename one.
 */
export type StopCause =
  /** What the patient reported partway through (Layer 2). */
  | 'symptom_red'
  | 'symptom_delta'
  | 'symptom_ceiling'
  /** What the monitor measured (Layer 3). */
  | 'safety_decay'
  /** The two ceilings in §7's safety settings. */
  | 'safety_duration'
  | 'session_duration';

export type SymptomVerdict =
  | { kind: 'continue' }
  | { kind: 'stop'; cause: StopCause; baseline: SymptomRating; rating: SymptomRating };

/**
 * Whether the session may continue after this answer.
 *
 * `baseline` is TODAY'S pre-session answer, never a fixed level and never
 * yesterday's. §9 Layer 2: a patient going from a bit off to clearly worse is
 * in the same trouble as one going from good to a bit off, and a rule written
 * on absolute values would catch the second and miss the first.
 */
export function symptomVerdict(
  baseline: SymptomRating,
  rating: SymptomRating,
  detail: number | null,
  safety: RunnerSafety,
): SymptomVerdict {
  // "Not good" ends the session whatever the settings say. This is the patient
  // telling us directly, and there is no threshold under which that is ignored.
  if (rating === 3) return { kind: 'stop', cause: 'symptom_red', baseline, rating };

  if (rating - baseline >= safety.symptomDeltaTrigger) {
    return { kind: 'stop', cause: 'symptom_delta', baseline, rating };
  }

  // The 0-10 backstop, applied only where a number actually exists — the detail
  // form opens on a bad answer and not otherwise (§8), so most checkpoints have
  // no number and this rule correctly says nothing about them.
  if (detail !== null && detail >= safety.symptomAbsoluteCeiling) {
    return { kind: 'stop', cause: 'symptom_ceiling', baseline, rating };
  }

  return { kind: 'continue' };
}

/**
 * Whether the session has run past its ceiling (§7).
 *
 * Checked at the boundary between exercises rather than mid-exercise: stopping
 * an exercise part-way to satisfy a whole-session limit would throw away a
 * measurement to enforce a number that is a backstop rather than a dose. The
 * single-exercise cap is what stops one running long, and that one does fire
 * mid-exercise.
 */
export function sessionOverran(
  startedAtMs: number,
  nowMs: number,
  safety: RunnerSafety,
): boolean {
  return nowMs - startedAtMs >= safety.maxSessionMinutes * 60_000;
}
